import React, { useContext, ReactNode } from 'react';
import { useLocalStorage } from '@mantine/hooks';
import { ForeignLanguage, Language } from '@/types/types';
import { isDefaultLanguage } from '@/utils/utilities';
import APP_CONFIG from '@/configs/appConfig';

interface Context {
	language: Language;
	setLanguage: (lang: Language) => void;
	getEntityText: <
		TObj extends {
			translations: Partial<
				Record<ForeignLanguage, Partial<Record<keyof TObj, string>>>
			> | null;
		}
	>(
		obj: TObj,
		key: keyof TObj
	) => string;
}

const ContentContext = React.createContext<Context>(null!);

export const useLanguage = () => useContext(ContentContext);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
	const [language, setLanguage] = useLocalStorage<Language>({
		key: 'language',
		defaultValue: APP_CONFIG.DEFAULT_LANGUAGE,
		getInitialValueInEffect: false,
	});

	const getEntityText: Context['getEntityText'] = (obj, key) => {
		if (isDefaultLanguage(language)) return obj[key] as string;

		return !!obj.translations &&
			!!obj.translations[language] &&
			!!obj.translations[language][key]
			? obj.translations[language][key]
			: (obj[key] as string);
	};

	return (
		<ContentContext.Provider
			value={{
				language,
				setLanguage,
				getEntityText,
			}}
		>
			{children}
		</ContentContext.Provider>
	);
};
